import type { Maybe } from '@adornis/base/utilTypes.js';
import { Entity, Field } from '@adornis/baseql/decorators.js';
import type { EntityData } from '@adornis/baseql/entities/types.js';
import { AdornisFilter } from '@adornis/filter/AdornisFilter.js';
import type { Filter } from 'mongodb';
import { CRMFlags } from '../CRMFlags.js';
import type { ChancenwerkUser } from '../master-data-management/users/entities/ChancenwerkUser.js';
import { ExternerMitarbeiter } from '../master-data-management/users/entities/ExternerMitarbeiter.js';
import { Mitarbeiter } from '../master-data-management/users/entities/Mitarbeiter.js';
import { Student } from '../master-data-management/users/entities/Student.js';
import { VerwaltungsMitarbeiter } from '../master-data-management/users/entities/VerwaltungsMitarbeiter.js';

@Entity()
export class CRMFilter extends AdornisFilter {
  static override _class = 'CRMFilter';

  constructor(doc: Partial<EntityData<CRMFilter>>) {
    super(doc);
  }

  @Field(() => CRMFlags, {
    default: v => v ?? new CRMFlags({}),
  })
  crmFlags!: CRMFlags;

  @Field(() => Boolean)
  active: Maybe<boolean>;

  @Field(() => String, {
    default: v => v ?? 'Alle',
  })
  class: Maybe<string>;

  private _buildNameCombinationsFilter(splitName: string[]) {
    const combinationsFilter: any[] = [];

    // Build any combination for n names in the name
    for (let i = 1; i < splitName.length; i++) {
      combinationsFilter.push({
        $and: [
          { vorname: { $regex: this._safeRegex(splitName.slice(0, i).join(' ')), $options: 'i' } },
          { nachname: { $regex: this._safeRegex(splitName.slice(i, splitName.length).join(' ')), $options: 'i' } },
        ],
      });
    }

    // Intermediate so we dont throw out partial given names
    combinationsFilter.push({
      $or: [
        { vorname: { $regex: this._safeRegex(splitName.join(' ')), $options: 'i' } },
        { nachname: { $regex: this._safeRegex(splitName.join(' ')), $options: 'i' } },
      ],
    });
    return combinationsFilter;
  }

  protected getNameFilter() {
    const filter = this.toObject();
    const filterName: Filter<ChancenwerkUser> = {};
    if (filter.search) {
      const searchComponents: string[] = filter.search.split(' ').filter(Boolean);
      if (searchComponents.length === 1 && searchComponents[0]) {
        filterName.$or = [
          { vorname: { $regex: this._safeRegex(searchComponents[0]), $options: 'i' } },
          { nachname: { $regex: this._safeRegex(searchComponents[0]), $options: 'i' } },
          { name: { $regex: this._safeRegex(filter.search), $options: 'i' } },
          { addressFullText: { $regex: this._safeRegex(filter.search), $options: 'i' } },
        ];
      } else if (searchComponents.length > 1) {
        filterName.$or = this._buildNameCombinationsFilter(searchComponents);
        filterName.$or.push({ name: { $regex: this._safeRegex(filter.search), $options: 'i' } });
      }
    }

    return filterName;
  }

  override getMongoFilter() {
    const filterCRMFlags: Filter<ChancenwerkUser> = {};

    const flagFields = Object.keys(this.crmFlags.fields).filter(f => f !== '_class' && f !== 'getAll');

    if (!this.crmFlags.getAll) {
      for (const field of flagFields) {
        if (this.crmFlags[field] !== null) {
          const proxy: Filter<ChancenwerkUser> = {};
          const partnerField = `flags.${field}`;
          proxy[partnerField] = this.crmFlags[field];
          if (!filterCRMFlags.$and?.length) filterCRMFlags.$and = [];
          filterCRMFlags.$and.push(proxy);
        }
      }
    } else {
      for (const field of flagFields) {
        const proxy: Filter<ChancenwerkUser> = {};
        proxy.$or = [];

        const proxySub: Filter<ChancenwerkUser> = {};
        const partnerField = `flags.${field}`;
        proxySub[partnerField] = false;

        const proxySubTwo: Filter<ChancenwerkUser> = {};
        const partnerFieldTwo = `flags.${field}`;
        proxySubTwo[partnerFieldTwo] = { $exists: false };

        const proxySubThree: Filter<ChancenwerkUser> = {};
        const partnerFieldThree = `flags.${field}`;
        proxySubThree[partnerFieldThree] = null;

        proxy.$or.push(proxySub);
        proxy.$or.push(proxySubTwo);
        proxy.$or.push(proxySubThree);
        if (!filterCRMFlags.$and?.length) filterCRMFlags.$and = [];
        filterCRMFlags.$and.push(proxy);
        filterCRMFlags.$and.push({
          _class: {
            $nin: [Student._class, Mitarbeiter._class, VerwaltungsMitarbeiter._class, ExternerMitarbeiter._class],
          },
        });
      }
    }

    if (!filterCRMFlags.$and) filterCRMFlags.$and = [];
    filterCRMFlags.$and.push(this.getNameFilter());

    return filterCRMFlags;
  }
}
